import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MENUITEM_IDS } from '@/lib';
import { StanButton } from '@/components';
import { useUser } from '@/queries/useUser';
import { useFunnelsData } from '@/queries/useStoreData';
import { useGlobalStore, useStoreStore } from '@/stores';
import { useStanRouter } from '@/hooks/useStanRouter';
import {
    DeleteFunnelModal,
    EditFunnelDetailsModal,
    FunnelEmptyState,
    FunnelsList,
    NoLinksiteModal,
} from '@/features/funnels/components';
import { useFunnelAnalytics } from '@/features/funnels/queries/useFunnelQueries';
import { useCreateFunnel, useDuplicateFunnel } from '@/features/funnels/queries/useFunnelMutations';
import { toFunnelListItem } from '@/features/funnels/logic';
import type { DeleteFunnelState, EditFunnelDetailsState, FunnelListItem } from '@/features/funnels/types';
import IcPlus from '@/assets/icons/ic-plus.svg';

const FunnelsPage = () => {
    const { user } = useUser();
    const { push } = useStanRouter();
    const setBreadcrumbs = useGlobalStore(state => state.setBreadcrumbs);

    // Data fetching
    const isFunnelAllowed = user?.subscription_tier_allowed_modules?.includes('funnels') ?? false;
    const { data: funnels = [], isLoading: isFunnelsLoading } = useFunnelsData(isFunnelAllowed);
    const { data: analyticsMap = {} } = useFunnelAnalytics();

    // Mutations
    const createFunnel = useCreateFunnel();
    const duplicateFunnel = useDuplicateFunnel();

    // Modal state
    const [editState, setEditState] = useState<EditFunnelDetailsState | null>(null);
    const [deleteState, setDeleteState] = useState<DeleteFunnelState | null>(null);
    const [showNoLinksiteModal, setShowNoLinksiteModal] = useState(false);

    // Funnel fetch reads from the linksite for theme fallback — guard against creating
    // one before the user has set up their store.
    const defaultStoreId = useStoreStore(state => state.defaultStoreId);
    const hasLinksite = !!defaultStoreId;

    // Breadcrumbs
    useEffect(() => {
        setBreadcrumbs([{ title: 'My Funnels', to: '/funnels', active: true, id: MENUITEM_IDS.FUNNELS }]);
    }, [setBreadcrumbs]);

    // Track the store_id of a newly duplicated funnel so we can scroll to it
    const pendingScrollId = useRef<number | null>(null);

    // Transform stores into display items — sorted newest first (highest store_id first)
    const funnelItems: FunnelListItem[] = useMemo(() => {
        if (!user?.username) return [];
        return funnels
            .map(store => toFunnelListItem(store, user.username, analyticsMap))
            .sort((a, b) => b.store_id - a.store_id);
    }, [funnels, user?.username, analyticsMap]);

    // After funnelItems update, scroll to the duplicated funnel if pending
    useEffect(() => {
        if (pendingScrollId.current === null) return;
        const targetId = pendingScrollId.current;
        // Check if the duplicated funnel is now in the list
        const exists = funnelItems.some(f => f.store_id === targetId);
        if (exists) {
            pendingScrollId.current = null;
            // Small delay to let the DOM render the new row
            requestAnimationFrame(() => {
                const el = document.getElementById(`funnel-card-${targetId}`);
                el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        }
    }, [funnelItems]);

    // Handlers
    const handleCreateFunnel = useCallback(() => {
        if (!hasLinksite) {
            setShowNoLinksiteModal(true);
            return;
        }
        push('/funnels/create');
    }, [push, hasLinksite]);

    const handleEdit = useCallback((funnel: FunnelListItem) => {
        setEditState({
            storeId: funnel.store_id,
            name: funnel.name,
            slug: funnel.slug,
        });
    }, []);

    const handleDuplicate = useCallback(
        (funnel: FunnelListItem) => {
            duplicateFunnel.mutate(funnel.store_id, {
                onSuccess: newStore => {
                    if (newStore?.store_id) {
                        pendingScrollId.current = newStore.store_id;
                    }
                },
            });
        },
        [duplicateFunnel]
    );

    const handleDelete = useCallback((funnel: FunnelListItem) => {
        setDeleteState({
            storeId: funnel.store_id,
            name: funnel.name,
        });
    }, []);

    const showEmptyState = !isFunnelsLoading && (funnelItems.length === 0 || !isFunnelAllowed);

    return (
        <>
            {showEmptyState ? (
                <FunnelEmptyState
                    onCreateFunnel={handleCreateFunnel}
                    isLoading={createFunnel.isPending}
                    isFunnelAllowed={isFunnelAllowed}
                />
            ) : (
                <div className='relative pb-8 md:pb-16 xl:pb-32'>
                    <div className='mb-4 flex justify-end sm:mb-6'>
                        <StanButton name='create-funnel-button' onClick={handleCreateFunnel} icon={IcPlus}>
                            Create New Funnel
                        </StanButton>
                    </div>
                    <FunnelsList
                        funnels={funnelItems}
                        onEdit={handleEdit}
                        onDuplicate={handleDuplicate}
                        onDelete={handleDelete}
                        isDuplicating={duplicateFunnel.isPending}
                    />
                </div>
            )}

            <EditFunnelDetailsModal
                isOpen={!!editState}
                onClose={() => setEditState(null)}
                storeId={editState?.storeId ?? 0}
                currentName={editState?.name ?? ''}
                currentSlug={editState?.slug ?? ''}
            />

            <DeleteFunnelModal
                isOpen={!!deleteState}
                onClose={() => setDeleteState(null)}
                storeId={deleteState?.storeId ?? 0}
                funnelName={deleteState?.name ?? ''}
            />

            <NoLinksiteModal isOpen={showNoLinksiteModal} onClose={() => setShowNoLinksiteModal(false)} />
        </>
    );
};

export default FunnelsPage;
